import React from 'react';
import { BadgeCheck, Sparkles, Star, TrendingUp, Heart, Award, Users, Crown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Badge tier configuration
const BADGE_TIERS = {
  new_creator: {
    label: 'New Creator',
    description: 'Just started their creative journey',
    icon: Sparkles,
    color: 'text-pastel-stickers',
    bgColor: 'bg-pastel-stickers/20',
    borderColor: 'border-pastel-stickers/30'
  },
  rising_star: {
    label: 'Rising Star',
    description: 'Growing fast with great reviews',
    icon: TrendingUp,
    color: 'text-pastel-keyrings',
    bgColor: 'bg-pastel-keyrings/20',
    borderColor: 'border-pastel-keyrings/30'
  },
  top_seller: {
    label: 'Top Seller',
    description: 'Consistently excellent sales and reviews',
    icon: Award,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30'
  },
  community_favourite: {
    label: 'Community Favourite',
    description: 'Loved by the community with many endorsements',
    icon: Heart,
    color: 'text-pastel-crochet',
    bgColor: 'bg-pastel-crochet/20',
    borderColor: 'border-pastel-crochet/30'
  }
};

// Founding Creator Badge
export const FoundingCreatorBadge = ({ size = 'default', showTooltip = true }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const badge = (
    <span className="inline-flex items-center justify-center">
      <Crown 
        className={`${sizeClasses[size]} text-amber-500 fill-amber-500/30`} 
        data-testid="founding-creator-badge"
      />
    </span>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-500" />
            <span className="font-semibold">Founding Creator</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            One of our earliest supporters who helped build Gathered Things from the beginning.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const FoundingCreatorBadgeFull = ({ className = '' }) => {
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/40 ${className}`}>
      <Crown className="w-4 h-4 text-amber-500 fill-amber-500/30" />
      <span className="text-xs font-semibold text-foreground">Founding Creator</span>
    </div>
  );
};

export const VerifiedBadge = ({ size = 'default', showTooltip = true }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const badge = (
    <span className="inline-flex items-center justify-center">
      <BadgeCheck 
        className={`${sizeClasses[size]} text-pastel-art fill-pastel-art/30`} 
        data-testid="verified-badge"
      />
    </span>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pastel-keyrings" />
            <span className="font-semibold">Verified Neurodivergent Creator</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            This creator has been verified as a member of the neurodivergent community.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const VerifiedBadgeFull = ({ className = '' }) => {
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pastel-art/20 border border-pastel-art/30 ${className}`}>
      <BadgeCheck className="w-4 h-4 text-pastel-art fill-pastel-art/30" />
      <span className="text-xs font-semibold text-foreground">Verified Creator</span>
    </div>
  );
};

export const PendingBadge = ({ className = '' }) => {
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pastel-keyrings/20 border border-pastel-keyrings/30 ${className}`}>
      <span className="text-xs font-semibold text-foreground">Verification Pending</span>
    </div>
  );
};

// Tier Badge Component
export const TierBadge = ({ tier, size = 'default', showTooltip = true }) => {
  const tierConfig = BADGE_TIERS[tier];
  if (!tierConfig) return null;

  const Icon = tierConfig.icon;
  const sizeClasses = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const badge = (
    <span className={`inline-flex items-center justify-center ${tierConfig.color}`}>
      <Icon className={sizeClasses[size]} />
    </span>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${tierConfig.color}`} />
            <span className="font-semibold">{tierConfig.label}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {tierConfig.description}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const TierBadgeFull = ({ tier, className = '' }) => {
  const tierConfig = BADGE_TIERS[tier];
  if (!tierConfig) return null;

  const Icon = tierConfig.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${tierConfig.bgColor} border ${tierConfig.borderColor} ${className}`}>
      <Icon className={`w-4 h-4 ${tierConfig.color}`} />
      <span className="text-xs font-semibold text-foreground">{tierConfig.label}</span>
    </div>
  );
};

// Endorsement Badge
export const EndorsementBadge = ({ count, showTooltip = true }) => {
  if (!count || count < 1) return null;

  const badge = (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pastel-crochet/20 border border-pastel-crochet/30">
      <Users className="w-3 h-3 text-pastel-crochet" />
      <span className="text-xs font-semibold">{count}</span>
    </span>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-semibold">{count} Creator Endorsement{count > 1 ? 's' : ''}</p>
          <p className="text-sm text-muted-foreground">
            Other verified creators vouch for this seller
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Combined Badge Display for Store/Product
export const CreatorBadges = ({ isVerified, isFoundingCreator, tier, endorsements, size = 'default' }) => {
  return (
    <span className="inline-flex items-center gap-1">
      {isFoundingCreator && <FoundingCreatorBadge size={size} />}
      {isVerified && <VerifiedBadge size={size} />}
      {tier && <TierBadge tier={tier} size={size} />}
      {endorsements > 0 && <EndorsementBadge count={endorsements} />}
    </span>
  );
};

// Full Badge Section for Store Page
export const CreatorBadgeSection = ({ isVerified, isFoundingCreator, tier, endorsements, endorsers = [] }) => {
  return (
    <div className="flex flex-wrap gap-2">
      {isFoundingCreator && <FoundingCreatorBadgeFull />}
      {isVerified && <VerifiedBadgeFull />}
      {tier && <TierBadgeFull tier={tier} />}
      {endorsements > 0 && (
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pastel-crochet/20 border border-pastel-crochet/30">
          <Users className="w-4 h-4 text-pastel-crochet" />
          <span className="text-xs font-semibold text-foreground">
            {endorsements} Endorsement{endorsements > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
};

export { BADGE_TIERS };
